import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { campaignService } from '../services/api';
import CampaignDetails from '../components/CampaignDetails';
import RegistrationForm from '../components/RegistrationForm';
import VoucherConfirmation from '../components/VoucherConfirmation';
import './CampaignPage.css';

function CampaignPage() {
  const { uuid } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('details');

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        setLoading(true);
        const response = await campaignService.getCampaign(uuid);
        setCampaign(response.data);
        setError(null);

        // Save tenant branding to localStorage & notify header
        const tenant = response.data.tenant;
        if (tenant) {
          const tenantData = {
            name: tenant.name,
            slug: tenant.slug,
            logo_url: tenant.logo_url,
            subtitle: tenant.subtitle,
            primary_color: tenant.primary_color,
            secondary_color: tenant.secondary_color,
          };
          localStorage.setItem('spe_tenant', JSON.stringify(tenantData));
          window.dispatchEvent(new CustomEvent('tenant-loaded', { detail: tenantData }));

          // Apply tenant branding colors
          const root = document.documentElement;
          if (tenant.primary_color) {
            root.style.setProperty('--color-primary', tenant.primary_color);
            root.style.setProperty('--color-primary-dark', tenant.primary_color);
          }
          if (tenant.secondary_color) {
            root.style.setProperty('--color-secondary', tenant.secondary_color);
          }
        }

        // Check if user already has a voucher for this campaign in localStorage
        const savedVoucher = localStorage.getItem(`spe_voucher_${uuid}`);
        if (savedVoucher) {
          try {
            const parsed = JSON.parse(savedVoucher);
            setVoucher(parsed);
            setStep('confirmation');
          } catch {}
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Gagal memuatkan kempen');
      } finally {
        setLoading(false);
      }
    };

    if (uuid) fetchCampaign();

    return () => {
      const root = document.documentElement;
      root.style.removeProperty('--color-primary');
      root.style.removeProperty('--color-primary-dark');
      root.style.removeProperty('--color-secondary');
    };
  }, [uuid]);

  const handleRegisterSubmit = async (formData) => {
    const response = await campaignService.registerCustomer(uuid, formData);
    const voucherData = response.data.voucher;
    setVoucher(voucherData);

    // Save voucher + campaign info to localStorage
    const voucherWithCampaign = {
      ...voucherData,
      campaign_uuid: uuid,
      campaign_name: campaign.name,
      campaign_type: campaign.campaign_type,
      discount_percentage: campaign.discount_percentage,
      cashback_amount: campaign.cashback_amount,
      reward_title: campaign.reward_title,
      all_branches: campaign.all_branches,
      branches: campaign.branches,
    };
    localStorage.setItem(`spe_voucher_${uuid}`, JSON.stringify(voucherWithCampaign));

    // Maintain registered campaigns list
    let registered = [];
    try {
      registered = JSON.parse(localStorage.getItem('spe_registered_campaigns') || '[]');
    } catch {}
    if (!registered.find(r => r.uuid === uuid)) {
      registered.push({ uuid, name: campaign.name });
      localStorage.setItem('spe_registered_campaigns', JSON.stringify(registered));
    }

    // Save customer info
    localStorage.setItem('spe_customer', JSON.stringify({
      phone_number: formData.phone_number,
      full_name: formData.full_name,
    }));

    setStep('confirmation');
  };

  if (loading) {
    return (
      <div className="campaign-loading animate-fade-in">
        <div className="skeleton skeleton-logo" />
        <div className="skeleton skeleton-title" />
        <div className="skeleton skeleton-banner" />
        <div className="skeleton skeleton-text" />
        <div className="skeleton skeleton-text short" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="campaign-error animate-fade-in">
        <div className="error-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h2>Sesuatu telah berlaku</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="campaign-page animate-fade-in">
      {step === 'details' && campaign && (
        <>
          <CampaignDetails campaign={campaign} />
          <button
            className="btn-register animate-fade-in-up stagger-3"
            onClick={() => setStep('register')}
            disabled={campaign.available_slots <= 0}
          >
            {campaign.available_slots > 0 ? 'Daftar Sekarang' : 'Pendaftaran Ditutup'}
          </button>
        </>
      )}

      {step === 'register' && campaign && (
        <RegistrationForm
          campaign={campaign}
          onSubmit={handleRegisterSubmit}
          onBack={() => setStep('details')}
        />
      )}

      {step === 'confirmation' && voucher && (
        <VoucherConfirmation voucher={voucher} campaign={campaign} />
      )}
    </div>
  );
}

export default CampaignPage;
